import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const cyberArk = defineConnector({
  identifier: "cyberArk",
  name: "CyberArk",
  tagLine: "Privileged access management",
  logo,
  protocol: "http",
  tags: ["it", "security", "identity"],
  defaultUrlPattern: () =>
    "https://.*\\.privilegecloud\\.cyberark\\.(cloud|com)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an OAuth service user",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Log in to your [CyberArk Identity Administration](https://identity.cyberark.cloud) portal
            2. Navigate to **Users** and create a new service user
            3. Enable the **Is OAuth confidential client** checkbox
            4. Assign the service user to a role with the desired Privilege Cloud permissions
          `,
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          helperText: "The service user login name.",
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          helperText: "The service user password.",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "hostname",
          label: "Identity domain",
          required: true,
          placeholder: "abc1234",
          helperText:
            "Your CyberArk Identity tenant ID (the domain of id.cyberark.cloud).",
          target: { kind: "configuration", field: "identityDomain" },
        },
        {
          kind: "hostname",
          label: "Privilege Cloud domain",
          required: true,
          placeholder: "mycompany",
          helperText:
            "The domain of your Privilege Cloud tenant (e.g. mycompany.privilegecloud.cyberark.cloud).",
          target: { kind: "configuration", field: "domain" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ identityDomain }) => {
      const tenantId = identityDomain;
      return `https://${tenantId}/oauth2/platformtoken`;
    },
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ domain }) => ({
      url: `https://${domain}/PasswordVault/API/auth/methods`,
      expectedStatus: [200, 403],
    }),
  },
  publicEnvVars: ({ domain, identityDomain }) => ({
    CYBER_ARK_URL: domain ? `https://${domain}` : "",
    CYBER_ARK_IDENTITY_URL: identityDomain ? `https://${identityDomain}` : "",
  }),
});
